import { useRoles } from '../../hooks/useRoles';
import {
  AllocationTargetField,
  type AllocationTarget,
} from '../admin/AllocationTargetField';
import { RoleCombobox } from './RoleCombobox';
import type { PersonDetailsValue } from './personDetails';

export interface PersonDetailsFormProps {
  value: PersonDetailsValue;
  onChange: (next: PersonDetailsValue) => void;
  target: AllocationTarget;
  onTargetChange: (next: string | null) => void;
  /**
   * The wizard is an onboarding flow by definition, so it hides the
   * "existing staff" escape hatch; the one-off add page keeps it.
   */
  showExistingStaffToggle?: boolean;
  autoFocus?: boolean;
}

/**
 * The person intake fields, shared by the one-off add page and step 1 of the
 * guided new-employee wizard. Purely controlled: the parent owns the value
 * and decides what submitting means.
 */
export function PersonDetailsForm({
  value,
  onChange,
  target,
  onTargetChange,
  showExistingStaffToggle = true,
  autoFocus = false,
}: PersonDetailsFormProps) {
  const roles = useRoles();
  const set = <K extends keyof PersonDetailsValue>(
    key: K,
    next: PersonDetailsValue[K],
  ) => onChange({ ...value, [key]: next });

  return (
    <div className="space-y-4">
      <AllocationTargetField value={target} onChange={onTargetChange} />

      {showExistingStaffToggle ? (
        <label className="flex items-start gap-2.5 rounded-md border border-line/10 bg-surface-2/40 px-4 py-3 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={value.onboardingHidden}
            onChange={(e) => set('onboardingHidden', e.target.checked)}
          />
          <span>
            <span className="font-semibold text-fg">
              Existing staff (not an onboarding starter)
            </span>
            <span className="mt-1 block text-xs text-fg-muted">
              For current employees attending only to sign in and collect kit.
              Skips onboarding tasks and keeps them off the onboarding
              dashboard. They still appear in the register and, given a role,
              get a kit checklist. Only name, email and role are needed.
            </span>
          </span>
        </label>
      ) : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="First name" htmlFor="first_name" required>
          <input
            id="first_name"
            value={value.firstName}
            onChange={(e) => set('firstName', e.target.value)}
            autoFocus={autoFocus}
          />
        </Field>
        <Field label="Last name" htmlFor="last_name" required>
          <input
            id="last_name"
            value={value.lastName}
            onChange={(e) => set('lastName', e.target.value)}
          />
        </Field>
      </div>

      <Field
        label="Work email"
        htmlFor="email"
        required
        hint="Must be unique. Used for SSO matching and self-service once they have a Corrigenda account."
      >
        <input
          id="email"
          type="email"
          value={value.email}
          onChange={(e) => set('email', e.target.value)}
          placeholder="jamie.reed@corrigenda.co.uk"
        />
      </Field>

      <Field
        label="Personal email"
        htmlFor="personal_email"
        hint="The pre-start address the welcome / sign-in code email is sent to. Defaults to the work email if left blank."
      >
        <input
          id="personal_email"
          type="email"
          value={value.personalEmail}
          onChange={(e) => set('personalEmail', e.target.value)}
          placeholder="jamie.reed@gmail.com"
        />
      </Field>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Employee ID" htmlFor="employee_id">
          <input
            id="employee_id"
            value={value.employeeId}
            onChange={(e) => set('employeeId', e.target.value)}
            placeholder="E1234"
            className="font-mono"
          />
        </Field>
        <Field
          label="Role"
          htmlFor="role_id"
          hint="Drives the onboarding template and kit checklist. Typing a role that is not listed adds it and notifies IT to create it in SAP."
        >
          <RoleCombobox
            id="role_id"
            roles={roles.data ?? []}
            valueId={value.roleId}
            onChange={(roleId) => set('roleId', roleId)}
          />
        </Field>
        <Field label="Manager" htmlFor="manager">
          <input
            id="manager"
            value={value.manager}
            onChange={(e) => set('manager', e.target.value)}
          />
        </Field>
        <Field label="Contract" htmlFor="contract">
          <input
            id="contract"
            value={value.contract}
            onChange={(e) => set('contract', e.target.value)}
            placeholder="HCC"
          />
        </Field>
        <Field label="Start date" htmlFor="start_date" hint="DD/MM/YYYY">
          <input
            id="start_date"
            type="date"
            value={value.startDate}
            onChange={(e) => set('startDate', e.target.value)}
          />
        </Field>
      </div>

      <Field
        label="Notes"
        htmlFor="notes"
        hint="Optional. Anything else worth recording at intake."
      >
        <textarea
          id="notes"
          value={value.notes}
          onChange={(e) => set('notes', e.target.value)}
          rows={3}
        />
      </Field>
    </div>
  );
}

interface FieldProps {
  label: string;
  htmlFor: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}

function Field({ label, htmlFor, hint, required, children }: FieldProps) {
  return (
    <label htmlFor={htmlFor} className="block">
      <div className="mb-1 flex items-center gap-1 text-xs font-medium text-fg-muted">
        {label}
        {required ? <span className="text-danger">*</span> : null}
      </div>
      <div
        className={[
          'rounded-md border border-line-strong/20 bg-surface shadow-sm focus-within:border-slate-500 focus-within:ring-2 focus-within:ring-slate-900 focus-within:ring-offset-1 transition-colors',
          '[&>input]:block [&>input]:w-full [&>input]:bg-transparent [&>input]:px-3 [&>input]:text-sm [&>input]:outline-none [&>input]:h-10',
          '[&>textarea]:block [&>textarea]:w-full [&>textarea]:bg-transparent [&>textarea]:px-3 [&>textarea]:py-2 [&>textarea]:text-sm [&>textarea]:outline-none',
        ].join(' ')}
      >
        {children}
      </div>
      {hint ? <p className="mt-1 text-[11px] text-fg-muted">{hint}</p> : null}
    </label>
  );
}
